<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { useDialogPluginComponent } from 'quasar';
import { computed, ref, toRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import { useLanguageValidationErrors } from '@/composables';
import { localizableString, localizableEditorString } from '@/helpers/validation.helper';
import type { FaqDto, Locales } from '@/services/types/api.types';
import { useFaqStore } from '@/stores/faqs.store';
import { wrapSavingOperation } from '@/helpers/notifications.helper';

import LanguageSwitcher from '../LanguageSwitcher.vue';
import LocalizedInput from '../LocalizedInput.vue';
import LocalizedEditor from '../base/LocalizedEditor.vue';
import { provideEditLocale } from '@/composables/data-display-utils';

// STORE
const { t } = useI18n();
const faqStore = useFaqStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  faq: FaqDto;
  languageEnabled: Locales[];
}>();

// EMITS
defineEmits([...useDialogPluginComponent.emits]);

// CONST
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const initialValues = props.faq;
const { handleSubmit, errors } = useForm({
  initialValues: initialValues,
  validationSchema: yup.object<FaqDto>({
    question: localizableString(props.languageEnabled),
    answer: localizableEditorString(props.languageEnabled),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editModel = ref(cloneDeep(props.faq));
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// COMPUTED
const dialogTitle = computed(() => (props.mode === 'add' ? t('faq.dialogs.create') : t('faq.dialogs.edit')));

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await faqStore.saveFaq(values),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="editModel" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="question"
          :label="t('global.label.question')"
          :editLocale="editLocale"
          :language_enabled="languageEnabled"
          outlined
        />
        <q-field :label="t('global.label.answer')" stack-label borderless>
          <template v-slot:control>
            <localized-editor
              name="answer"
              :label="t('global.label.answer')"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
